import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription 
} from '../../components/ui/dialog';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Building2,
  Mail,
  Phone,
  MapPin,
  FileText,
  ToggleLeft,
  ToggleRight,
  MoreVertical,
  Eye,
  Package,
  Grid,
  List,
  ExternalLink,
  Play,
  CheckCircle,
  Clock,
  Pause
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuração de cores e labels de estado para OFs
const STATUS_CONFIG = {
  rascunho: { label: 'Rascunho', bg: 'bg-stone-100', text: 'text-stone-600', icon: FileText },
  pendente: { label: 'Ready to Start', bg: 'bg-blue-100', text: 'text-blue-600', icon: CheckCircle },
  aguardar_entrada: { label: 'Aguardar Entrada', bg: 'bg-amber-100', text: 'text-amber-600', icon: Clock },
  em_curso: { label: 'Em Curso', bg: 'bg-emerald-100', text: 'text-emerald-600', icon: Play },
  pausado: { label: 'Pausado', bg: 'bg-orange-100', text: 'text-orange-600', icon: Pause },
  terminado: { label: 'Terminado', bg: 'bg-sky-100', text: 'text-sky-600', icon: CheckCircle },
  cancelado: { label: 'Cancelado', bg: 'bg-red-100', text: 'text-red-600', icon: null },
};

const emptyForm = {
  nome: '',
  codigo: '',
  email: '',
  telefone: '',
  morada: '',
  nif: '',
  notas: '',
  ativo: true
};

export default function GestaoMarcas() {
  const navigate = useNavigate();
  const [marcas, setMarcas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedMarca, setSelectedMarca] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filtroAtivo, setFiltroAtivo] = useState('todos'); // todos, ativos, inativos
  const [ofsMarca, setOfsMarca] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'

  useEffect(() => {
    loadMarcas();
    
    // Verificar se tem parâmetro new=1 para abrir modal de criação
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('new') === '1') {
      setFormData(emptyForm);
      setEditingId(null);
      setShowForm(true);
      // Limpar o parâmetro da URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadMarcas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/clientes`);
      setMarcas(response.data);
    } catch (error) {
      console.error('Erro ao carregar marcas:', error);
      toast.error('Erro ao carregar marcas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim()) {
      toast.error('O nome é obrigatório');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/clientes/${editingId}`, formData);
        toast.success('Marca atualizada com sucesso');
      } else {
        await axios.post(`${API}/clientes`, formData);
        toast.success('Marca criada com sucesso');
      }
      setShowForm(false);
      setFormData(emptyForm);
      setEditingId(null);
      loadMarcas();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao guardar marca'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (marca) => {
    setFormData({
      nome: marca.nome || '',
      codigo: marca.codigo || '',
      email: marca.email || '',
      telefone: marca.telefone || '',
      morada: marca.morada || '',
      nif: marca.nif || '',
      notas: marca.notas || '',
      ativo: marca.ativo !== false
    });
    setEditingId(marca.id);
    setShowForm(true);
  };

  const handleDelete = async (marca) => {
    if (!window.confirm(`Tem a certeza que deseja eliminar a marca "${marca.nome}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/clientes/${marca.id}`);
      toast.success('Marca eliminada com sucesso');
      loadMarcas();
    } catch (error) {
      toast.error('Erro ao eliminar marca');
    }
  };

  const handleViewDetails = async (marca) => {
    setSelectedMarca(marca);
    setShowDetails(true);
    
    // Carregar OFs da marca
    try {
      const response = await axios.get(`${API}/ofs`);
      const ofs = response.data.filter(of => 
        of.cliente?.toLowerCase() === marca.nome?.toLowerCase()
      );
      setOfsMarca(ofs);
    } catch (error) {
      console.error('Erro ao carregar OFs:', error);
      setOfsMarca([]);
    }
  };

  const toggleAtivo = async (marca) => {
    try {
      await axios.put(`${API}/clientes/${marca.id}`, {
        ...marca,
        ativo: !marca.ativo
      });
      toast.success(`Marca ${marca.ativo ? 'desativada' : 'ativada'}`);
      loadMarcas();
    } catch (error) {
      toast.error('Erro ao atualizar estado');
    }
  };

  // Filtrar marcas
  const marcasFiltradas = marcas.filter(marca => {
    const matchSearch = !searchTerm || 
      marca.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      marca.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      marca.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      marca.nif?.includes(searchTerm);
    
    let matchAtivo = true;
    if (filtroAtivo === 'ativos') matchAtivo = marca.ativo !== false;
    if (filtroAtivo === 'inativos') matchAtivo = marca.ativo === false;
    
    return matchSearch && matchAtivo;
  });

  // Navegar para a OF específica
  const handleNavigateToOF = (of) => {
    navigate(`/gestor/ordens-fabrico?of=${of.id}&tab=acompanhar`);
  };

  // Obter badge de estado da OF com cores diferenciadas
  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pendente;
    const Icon = config.icon;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${config.bg} ${config.text}`}>
        {Icon && <Icon className="h-3 w-3" />}
        {config.label}
      </span>
    );
  };

  return (
    <GestorLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900 mb-2">
              Gestão de Marcas
            </h1>
            <p className="text-stone-600">
              Gerir a base de dados de marcas da empresa
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Toggle Vista */}
            <div className="flex border rounded-full overflow-hidden">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="rounded-none"
                title="Vista em grelha"
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-none"
                title="Vista em lista"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            <Button 
              onClick={() => {
                setFormData(emptyForm);
                setEditingId(null);
                setShowForm(true);
              }}
              className="rounded-full"
              data-testid="btn-nova-marca"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Marca
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                placeholder="Pesquisar por nome, código, email ou NIF..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-marcas"
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant={filtroAtivo === 'todos' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroAtivo('todos')}
                className="rounded-full"
              >
                Todos ({marcas.length})
              </Button>
              <Button
                variant={filtroAtivo === 'ativos' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroAtivo('ativos')}
                className="rounded-full"
              >
                Ativos ({marcas.filter(c => c.ativo !== false).length})
              </Button>
              <Button
                variant={filtroAtivo === 'inativos' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroAtivo('inativos')}
                className="rounded-full"
              >
                Inativos ({marcas.filter(c => c.ativo === false).length})
              </Button>
            </div>
          </div>
        </Card>

        {/* Lista de Marcas */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : marcasFiltradas.length === 0 ? (
          <Card className="p-8 text-center">
            <Building2 className="h-12 w-12 mx-auto text-stone-300 mb-3" />
            <p className="text-stone-500">Nenhuma marca encontrada</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => {
                setFormData(emptyForm);
                setEditingId(null);
                setShowForm(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Criar primeira marca
            </Button>
          </Card>
        ) : viewMode === 'grid' ? (
          /* Vista em Grid (Cards) */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {marcasFiltradas.map((marca) => (
              <Card 
                key={marca.id} 
                className={`p-4 hover:shadow-md transition-shadow ${
                  marca.ativo === false ? 'opacity-60 bg-stone-50' : ''
                }`}
                data-testid={`marca-card-${marca.codigo}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      marca.ativo === false ? 'bg-stone-200' : 'bg-primary/10'
                    }`}>
                      <Building2 className={`h-6 w-6 ${
                        marca.ativo === false ? 'text-stone-400' : 'text-primary'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-stone-900">{marca.nome}</h3>
                      <p className="text-xs text-stone-500 font-mono">{marca.codigo}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleViewDetails(marca)}
                      title="Ver detalhes"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleEdit(marca)}
                      title="Editar"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  {marca.email && (
                    <div className="flex items-center gap-2 text-stone-600">
                      <Mail className="h-4 w-4 text-stone-400" />
                      <span className="truncate">{marca.email}</span>
                    </div>
                  )}
                  {marca.telefone && (
                    <div className="flex items-center gap-2 text-stone-600">
                      <Phone className="h-4 w-4 text-stone-400" />
                      <span>{marca.telefone}</span>
                    </div>
                  )}
                  {marca.nif && (
                    <div className="flex items-center gap-2 text-stone-600">
                      <FileText className="h-4 w-4 text-stone-400" />
                      <span>NIF: {marca.nif}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-stone-100">
                  <button
                    onClick={() => toggleAtivo(marca)}
                    className={`flex items-center gap-2 text-xs font-medium ${
                      marca.ativo === false ? 'text-stone-400' : 'text-green-600'
                    }`}
                  >
                    {marca.ativo === false ? (
                      <ToggleLeft className="h-5 w-5" />
                    ) : (
                      <ToggleRight className="h-5 w-5" />
                    )}
                    {marca.ativo === false ? 'Inativo' : 'Ativo'}
                  </button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(marca)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          /* Vista em Lista (Tabela) */
          <Card className="overflow-hidden">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Marca</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Código</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Telefone</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-stone-600">Estado</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-stone-600">Ações</th>
                </tr>
              </thead>
              <tbody>
                {marcasFiltradas.map((marca) => (
                  <tr 
                    key={marca.id} 
                    className={`border-t border-stone-100 hover:bg-stone-50 ${marca.ativo === false ? 'opacity-60' : ''}`}
                    data-testid={`marca-row-${marca.codigo}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          marca.ativo === false ? 'bg-stone-200' : 'bg-primary/10'
                        }`}>
                          <Building2 className={`h-5 w-5 ${
                            marca.ativo === false ? 'text-stone-400' : 'text-primary'
                          }`} />
                        </div>
                        <span className="font-semibold text-stone-900">{marca.nome}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-stone-600">{marca.codigo || '-'}</td>
                    <td className="py-3 px-4 text-stone-600">{marca.email || '-'}</td>
                    <td className="py-3 px-4 text-stone-600">{marca.telefone || '-'}</td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => toggleAtivo(marca)}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          marca.ativo === false ? 'bg-stone-100 text-stone-500' : 'bg-green-100 text-green-600'
                        }`}
                      >
                        {marca.ativo === false ? 'Inativo' : 'Ativo'}
                      </button>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(marca)}
                          title="Ver detalhes"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(marca)}
                          title="Editar"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:bg-red-50"
                          onClick={() => handleDelete(marca)}
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      {/* Dialog - Formulário */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingId ? 'Editar Marca' : 'Nova Marca'}
            </DialogTitle>
            <DialogDescription>
              {editingId ? 'Atualize os dados da marca' : 'Preencha os dados da nova marca'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Nome *
                </label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Nome da marca"
                  data-testid="input-nome"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Código
                </label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                  placeholder="Auto-gerado"
                  data-testid="input-codigo"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  NIF
                </label>
                <Input
                  value={formData.nif}
                  onChange={(e) => setFormData({ ...formData, nif: e.target.value })}
                  placeholder="Número de contribuinte"
                  data-testid="input-nif"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Email
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@exemplo.com"
                  data-testid="input-email"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Telefone
                </label>
                <Input
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder="+351 ..."
                  data-testid="input-telefone"
                />
              </div>
              
              <div className="col-span-2">
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Morada
                </label>
                <Input
                  value={formData.morada}
                  onChange={(e) => setFormData({ ...formData, morada: e.target.value })}
                  placeholder="Endereço completo"
                  data-testid="input-morada"
                />
              </div>
              
              <div className="col-span-2">
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Notas
                </label>
                <textarea
                  value={formData.notas}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  placeholder="Observações sobre a marca..."
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary min-h-[80px]"
                  data-testid="input-notas"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowForm(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={saving}
              >
                {saving ? 'A guardar...' : (editingId ? 'Atualizar' : 'Criar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog - Detalhes */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              {selectedMarca?.nome}
            </DialogTitle>
            <DialogDescription>
              Código: {selectedMarca?.codigo}
            </DialogDescription>
          </DialogHeader>

          {selectedMarca && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {selectedMarca.email && (
                  <div>
                    <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Email</p>
                    <p className="text-sm text-stone-900">{selectedMarca.email}</p>
                  </div>
                )}
                {selectedMarca.telefone && (
                  <div>
                    <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Telefone</p>
                    <p className="text-sm text-stone-900">{selectedMarca.telefone}</p>
                  </div>
                )}
                {selectedMarca.nif && (
                  <div>
                    <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">NIF</p>
                    <p className="text-sm text-stone-900">{selectedMarca.nif}</p>
                  </div>
                )}
                {selectedMarca.morada && (
                  <div className="col-span-2">
                    <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Morada</p>
                    <p className="text-sm text-stone-900">{selectedMarca.morada}</p>
                  </div>
                )}
              </div>

              {selectedMarca.notas && (
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Notas</p>
                  <p className="text-sm text-stone-700">{selectedMarca.notas}</p>
                </div>
              )}

              {/* OFs da Marca */}
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">
                  Ordens de Fabrico ({ofsMarca.length})
                </p>
                {ofsMarca.length === 0 ? (
                  <p className="text-sm text-stone-400">Nenhuma OF encontrada</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {ofsMarca.slice(0, 15).map(of => {
                      const statusConfig = STATUS_CONFIG[of.status] || STATUS_CONFIG.pendente;
                      const StatusIcon = statusConfig.icon;
                      return (
                        <button
                          key={of.id}
                          onClick={() => handleNavigateToOF(of)}
                          className="w-full flex items-center justify-between p-2 bg-stone-50 rounded hover:bg-stone-100 transition-colors cursor-pointer group"
                          title={`Abrir OF ${of.numero_of}`}
                        >
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-stone-400" />
                            <span className="text-sm font-medium text-stone-800">{of.numero_of}</span>
                            <ExternalLink className="h-3 w-3 text-stone-300 group-hover:text-primary transition-colors" />
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${statusConfig.bg} ${statusConfig.text}`}>
                            {StatusIcon && <StatusIcon className="h-3 w-3" />}
                            {statusConfig.label}
                          </span>
                        </button>
                      );
                    })}
                    {ofsMarca.length > 15 && (
                      <p className="text-xs text-center text-stone-400 pt-2">
                        +{ofsMarca.length - 15} OFs adicionais
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDetails(false)}
                >
                  Fechar
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => {
                    setShowDetails(false);
                    handleEdit(selectedMarca);
                  }}
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Editar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </GestorLayout>
  );
}
